import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Laptop, Loader2, LogOut, Smartphone } from "lucide-react";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");

/** Your devices: everywhere this account is signed in, with sign-out. */
export default function DevicesCard() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const signOut = async (ids) => {
    setBusy(ids.length > 1 ? "others" : ids[0]);
    try {
      for (const id of ids) await api.delete(`/auth/sessions/${id}`);
      toast.success(ids.length > 1 ? "Signed out of your other devices" : "Device signed out");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const others = (rows || []).filter((r) => !r.current);
  return (
    <Card className="p-6 rounded-2xl border-stone-200 max-w-2xl" data-testid="devices-card">
      <h3 className="font-heading text-xl font-semibold">Your devices</h3>
      <p className="text-sm text-stone-500 mt-1">
        Everywhere your account is signed in. If you don't recognise a device, sign it out and change your password.
      </p>
      {rows === null ? (
        <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></div>
      ) : (
        <ul className="mt-4 divide-y divide-stone-100">
          {rows.map((r) => {
            const Icon = r.app.endsWith("_app") || r.app === "legacy" ? Smartphone : Laptop;
            return (
              <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`device-${r.id}`}>
                <Icon className="h-5 w-5 text-stone-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.device} {r.current && <Badge className="ml-1 bg-teal/10 text-teal border-0 rounded-full">This device</Badge>}
                  </div>
                  <div className="text-xs text-stone-500">{r.app_label} · signed in {when(r.created_at)} · last active {when(r.last_used_at)}</div>
                </div>
                {!r.current && (
                  <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => signOut([r.id])}>
                    {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign out"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {others.length > 1 && (
        <Button variant="outline" className="mt-4 rounded-full" disabled={!!busy} onClick={() => signOut(others.map((r) => r.id))} data-testid="devices-signout-others">
          {busy === "others" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogOut className="h-4 w-4 mr-2" />}Sign out all other devices
        </Button>
      )}
    </Card>
  );
}
